import { HighlightOff } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Fragment, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GlobalDataContext } from "../../../../../contexts/GlobalData";
import { LoadingContext } from "../../../../../contexts/Loading";
import { SnackbarContext } from "../../../../../contexts/Snackbar";
import { STATUSES } from "../../../../../enums/AppointmentStatus";
import {
  DELETE_APPOINTMENT,
  REJECT_PENDING,
} from "../../../../../enums/PermissionTitles";
import * as keys from "../../../../../enums/QueryKeys";
import { deleteAppointment } from "../../../../../enums/queryFunction/Delete";
import useConfirm from "../../../../../hooks/useConfirm";
import useUser, { useGetUserId } from "../../../../../hooks/useUser";
import ActionRow from "../../../../admin/clinic/components/actions/ActionRow";
import useAppointmentStatusChange from "../../hooks/useAppointmentStatusChange";
import {
  canChangePendingStatus,
  canManageAppointment,
  canMasterCancelNonSystem,
} from "../../utils/appointmentActionRules";

export function canCancelAppointment(appointment, roles, clinicType) {
  const isDone =
    appointment?.status === STATUSES.FINISHED ||
    appointment?.status === STATUSES.RESOLVED;
  return (
    canMasterCancelNonSystem(appointment, clinicType, roles) ||
    (!roles.master && canManageAppointment(appointment, roles) && !isDone)
  );
}

/**
 * Rejects a pending appointment when the user may do so, otherwise deletes
 * it and leaves the page.
 */
export default function CancelAppointment({ appointment, clinicType }) {
  const { t } = useTranslation();
  const { roles } = useUser();
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const { changeStatus, ConfirmationDialog: RejectConfirmationDialog } =
    useAppointmentStatusChange(appointment);
  const deletion = useDeleteAppointment(appointment);

  if (!canCancelAppointment(appointment, roles, clinicType)) {
    return null;
  }

  const hasPermission =
    isPermissionAllowed(REJECT_PENDING) ||
    isPermissionAllowed(DELETE_APPOINTMENT);

  function handleCancel() {
    if (!roles.master && canChangePendingStatus(appointment, isPermissionAllowed)) {
      changeStatus(STATUSES.INACTIVE);
    } else {
      deletion.deleteWithConfirmation();
    }
  }

  return (
    <Fragment>
      <ActionRow
        justButton
        color="error"
        icon={<HighlightOff />}
        label={t("CancelAppointment")}
        description={
          hasPermission
            ? t("CancelAppointmentDesc")
            : t("NoPermissionForThisAction")
        }
        disabled={!hasPermission}
        onClick={() => handleCancel()}
        testId={"appointment.details.delete-button"}
      />
      <RejectConfirmationDialog />
      <deletion.ConfirmationDialog />
    </Fragment>
  );
}

function useDeleteAppointment(appointment) {
  const { t } = useTranslation();
  const { roles } = useUser();
  const userId = useGetUserId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleSnackbar } = useContext(SnackbarContext);
  const { setIsLoading } = useContext(LoadingContext);
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel"),
    "warning",
  );

  function removeFromList(key) {
    queryClient.setQueryData([key], (oldData) => {
      if (!oldData) return oldData;
      return oldData.filter((item) => item.id !== appointment.id);
    });
  }

  function removeFromPetsAppointments(petId) {
    queryClient.setQueryData([keys.PETS_APPOINTMENTS + petId], (oldData) => {
      if (!oldData) return oldData;
      return {
        speciesAllowed: oldData.speciesAllowed,
        appointmentList: oldData.appointmentList.filter(
          (item) => item.id !== appointment.id,
        ),
      };
    });
  }

  const { mutate } = useMutation({
    mutationFn: () => deleteAppointment(appointment?.id, userId),
    onSuccess: () => {
      [keys.APPOINTMENTS, keys.PENDING_APPOINTMENTS].forEach(removeFromList);
      queryClient.invalidateQueries([keys.APPOINTMENT_GENERAL + appointment?.id]);
      removeFromPetsAppointments(appointment?.pet?.id);
      handleSnackbar("successMessage", "success");
      setIsLoading(false);
      navigate(-1);
    },
    onError: (error) => {
      const backendError = error?.response?.data;
      handleSnackbar(backendError ? backendError : "errorMessage", "error");
      setIsLoading(false);
    },
  });

  async function deleteWithConfirmation() {
    getMessage(
      roles.adminOrEmployee
        ? t("DeletingAppointmentAdmin")
        : t("CancelingAnAppointmentUser"),
    );
    if (await confirm()) {
      setIsLoading(true);
      mutate();
    }
  }

  return { deleteWithConfirmation, ConfirmationDialog };
}
